import { z } from "zod";
import { TemplateDocumentTypeSchema, TemplateLayoutSchema } from "./TemplateLayoutSchema";

export const TemplateGenerationStatusSchema = z.enum(["MANUAL", "NEEDS_REVIEW", "READY", "FAILED"]);

export const TemplateSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string().min(1, "Template name is required"),
  type: TemplateDocumentTypeSchema.exclude(["UNKNOWN"]),
  layoutJson: z.string().refine((val) => {
    if (val.startsWith('r2://')) return true;
    try {
      const parsed = JSON.parse(val);
      TemplateLayoutSchema.parse(parsed);
      return true;
    } catch (e) {
      return false;
    }
  }, "Invalid Layout JSON structure"),
  // Extracted invoice data (seller, buyer, line items, etc.) from AI extraction
  // Used for PDF preview generation with actual data instead of dummy data
  extractedDataJson: z.string().optional(),
  generationStatus: TemplateGenerationStatusSchema.default("MANUAL"),
  generationQualityJson: z.string().optional().nullable(),
  sourceTraceId: z.string().optional().nullable(),
  sourceFileName: z.string().optional().nullable(),
  isDefault: z.boolean().default(false),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Template = z.infer<typeof TemplateSchema>;
export type TemplateGenerationStatus = z.infer<typeof TemplateGenerationStatusSchema>;

// Only editable draft fields cross the write boundary. A loaded revision is mandatory.
export const TemplateUpdateSchema = TemplateSchema.pick({
  name: true, type: true, layoutJson: true, extractedDataJson: true,
  generationStatus: true, generationQualityJson: true, sourceTraceId: true,
  sourceFileName: true, isDefault: true,
}).partial().extend({
  expectedUpdatedAt: z.string().datetime(),
  name: z.string().trim().min(1).optional(),
  layoutJson: z.union([z.string(), TemplateLayoutSchema]).optional().refine(value => {
    if (value === undefined) return true;
    try { return TemplateLayoutSchema.safeParse(typeof value === "string" ? JSON.parse(value) : value).success; }
    catch { return false; }
  }, "Invalid template layout"),
}).strict();
export type TemplateUpdate = z.infer<typeof TemplateUpdateSchema>;

export const TemplateDraftSchema = TemplateUpdateSchema.omit({ expectedUpdatedAt: true });
